import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EditablePurchaseVATInvoice from './EditablePurchaseVATInvoice';

const EditPurchaseInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Helper function to format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      setLoading(true);
      setError(null);
      console.log('📍 EditPurchaseInvoice: Starting fetch with ID:', id);
      try {
        const fetchUrl = `${apiUrl}/api/pos/${id}`;
        console.log('📍 Fetching from:', fetchUrl);
        
        const response = await axios.get(fetchUrl);
        console.log('📍 response object:', response);
        console.log('📍 response.data:', response.data);
        
        // The API response structure is: { data: {...}, message: "..." }
        // So we need response.data.data, but let's also try response.data itself
        let po = response.data.data;
        
        // Fallback: if response.data.data is incomplete, try response.data directly
        if (!po || (po && !po.id && !po.po_no)) {
          console.warn('⚠️  response.data.data is incomplete, trying different extraction');
          // Check if all the PO fields are at the top level
          po = response.data;
          // If it has a message property, it's the wrapper - extract the actual data
          if (po.message && po.data) {
            po = po.data;
          }
        }
        
        console.log('📍 Final extracted PO object:', po);
        console.log('📍 PO keys:', Object.keys(po || {}));
        console.log('📍 PO.id:', po?.id);
        console.log('📍 PO.po_no:', po?.po_no);
        console.log('📍 PO.po_date:', po?.po_date);
        console.log('📍 PO.status:', po?.status);

        if (!po) {
          throw new Error('Could not extract PO data from response');
        }

        // Extract customer name from the customer object in the API response
        const customerName = po.customer?.name || po.customerName || '';
        console.log('📍 Customer Name extracted:', {
          from_customer_object: po.customer?.name,
          customer_object: po.customer,
          fallback: po.customerName,
          final: customerName,
        });

        // Transform the purchase order data to match the form structure
        const transformedData = {
          id: po.id,
          poNumber: po.po_no || po.poNumber || 'PO-001',
          poDate: formatDateForInput(po.po_date || po.date),
          status: po.status || 'Unpaid',
          mode: po.mode || null,
          supplierId: po.supplier_id || po.supplierId || null,
          customer_id: po.customer_id || po.customerId || null,
          customerName: customerName,

          // Supplier Information
          supplierName: po.supplier?.name || po.supplierName || '',
          supplierTrn: po.supplier?.trn || po.supplierTrn || '',
          supplierPhone: po.supplier?.phone || po.supplierPhone || '',
          supplierAddress: po.supplier?.address || po.supplierAddress || '',
          supplierCity: po.supplier?.city || po.supplierCity || '',
          supplierCountry: po.supplier?.country || po.supplierCountry || 'UAE',

          // Line Items
          items: (po.plines || po.items || []).map(line => ({
            description: line.description || '',
            quantity: parseFloat(line.quantity || 0),
            unit: line.unit || 'Unit',
            unit_price: parseFloat(line.unit_price || line.unitPrice || 0),
            discount_amount: parseFloat(line.discount_amount || line.discountAmount || 0),
            vat_percent: parseFloat(line.vat_percent || line.vatPercent || 0),
          })),

          // Notes
          notes: po.notes || '',
          
          // Currency
          currency: po.currency || 'AED',
        };

        console.log('📍 Transformed data:', transformedData);
        setInitialData(transformedData);
      } catch (err) {
        console.error('❌ Error fetching purchase order:', err);
        
        let errorMessage = 'Unable to load purchase order. Please try again later.';
        
        if (err.response) {
          // Server responded with error status
          console.error('❌ Server Error Response:', {
            status: err.response.status,
            statusText: err.response.statusText,
            data: err.response.data,
          });
          
          if (err.response.status === 404) {
            errorMessage = 'Purchase order not found. It may have been deleted.';
          } else if (err.response.status === 401) {
            errorMessage = 'Unauthorized. Please log in again.';
          } else if (err.response.status === 403) {
            errorMessage = 'You do not have permission to view this purchase order.';
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.request) {
          // Request made but no response received
          console.error('❌ No response received:', err.request);
          errorMessage = 'No response from server. Please check your connection.';
        } else {
          // Error in request setup
          console.error('❌ Request setup error:', err.message);
          errorMessage = err.message || 'An error occurred while loading the purchase order.';
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPurchaseOrder();
    } else {
      console.warn('⚠️ No ID provided to EditPurchaseInvoice');
      setError('No invoice ID provided');
      setLoading(false);
    }
  }, [id, apiUrl]);

  // Fetch customer information if customerName is missing but customer_id exists
  useEffect(() => {
    const fetchCustomerInfo = async () => {
      if (initialData?.customer_id && !initialData?.customerName) {
        try {
          console.log('📍 Fetching customer info for ID:', initialData.customer_id);
          const response = await axios.get(`${apiUrl}/api/customers/${initialData.customer_id}`);
          console.log('📍 Customer response:', response.data);
          
          const customer = response.data.data || response.data;
          
          if (customer && customer.name) {
            console.log('✅ Got customer name:', customer.name);
            setInitialData(prev => ({
              ...prev,
              customerName: customer.name,
            }));
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch customer info:', {
            status: err.response?.status,
            message: err.message,
          });
          // Don't show error - just keep the customer_id visible
        }
      }
    };

    if (initialData) {
      fetchCustomerInfo();
    }
  }, [initialData?.customer_id, apiUrl]);

  const handleEditSubmit = async (purchaseOrderData) => {
    try {
      console.log('📍 Starting PO update with data:', purchaseOrderData);
      
      // Prepare update payload
      const updatePayload = {
        po_no: purchaseOrderData.poNumber,
        supplier_id: purchaseOrderData.supplierId,
        customer_id: purchaseOrderData.customer_id,
        po_date: purchaseOrderData.poDate,
        status: purchaseOrderData.status,
        mode: purchaseOrderData.status === 'Unpaid' ? '' : (purchaseOrderData.mode || ''),
        notes: purchaseOrderData.notes,
        items: purchaseOrderData.items,
      };

      console.log('📍 Update payload:', updatePayload);
      
      // Send update request to API
      const updateUrl = `${apiUrl}/api/pos/${id}`;
      console.log('📍 Sending PUT request to:', updateUrl);
      
      const response = await axios.put(updateUrl, updatePayload);
      
      console.log('✅ Update response:', response.data);

      // Show success message and navigate back
      alert('Purchase Invoice updated successfully!');
      navigate('/purchases/history');
    } catch (err) {
      console.error('❌ Error updating purchase order:', err);
      console.error('❌ Error response:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
      });
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Error updating purchase order. Please try again.';
      
      alert(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading purchase invoice...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-900 font-bold text-lg mb-2">Error</h3>
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => navigate('/purchases/history')}
            className="btn btn-sm btn-error text-white"
          >
            Back to Purchase History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-8 text-white shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">Edit Purchase Invoice</h1>
              <p className="text-white/80">Invoice # {initialData?.poNumber}</p>
            </div>
            <button
              onClick={() => navigate('/purchases/history')}
              className="btn btn-ghost text-white hover:bg-white/20"
            >
              ← Back to History
            </button>
          </div>
        </div>

        {/* Form Component */}
        {initialData && (
          <EditablePurchaseVATInvoice
            initialData={initialData}
            onSubmit={handleEditSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditPurchaseInvoice;
